import React, {useContext, useEffect, useState} from 'react';
import ProductPreview from "../product_selection/ProductPreview";
import { buyNowContext } from "../BuyNow";
import stripeApi from "../../../../api/stripeApi";
import ShippingPrice from "../common/ShippingPrice";
import SalesTaxPrice from "../common/SalesTaxPrice";
import TotalPrice from "../common/TotalPrice";
import PromoCode from "../common/PromoCode";
import mailChimpApi, { MAIL_CHIMP_LIST_IDS, MAIL_CHIMP_TAGS, MAIL_CHIMP_TAGS_STATUS } from "../../../../api/mailChimpApi";
import {calcTotalPrice} from "../../utils/pricingHelper";
import ReferralCode from "../common/ReferralCode";
import {formattedNextChargeDate} from "../../utils/orderHelper";
import trackingScripts from "../../utils/trackingScripts";
import { FACEBOOK_EVENTS } from "../../utils/facebookHelper";
import {PRODUCT_CATEGORIES, PURCHASE_FLOWS} from "../../../../constants";
import ProductPrice from "../common/ProductPrice";
import LineItemDisplay from "../common/LineItemDisplay";

const ReviewStage = () => {
  const {
    PURCHASE_STAGES, setOrder, lineItems, setPurchaseStage, shippingStageState,
    paymentStageState, promoCode, salesTaxRates, referralCode, giftSenderInformation, isProduction,
    portal, scaleProducts, quantitiesEnabled, purchaseFlow, adminReferralTargetCode, npi, deferredUserCreate
  } = useContext(buyNowContext);

  const scaleLineItem = lineItems.find(li => li.product.category === PRODUCT_CATEGORIES.SCALE);
  const adminAccessLineItem = lineItems.find(li => li.product.category === PRODUCT_CATEGORIES.ADMIN_ACCESS);
  const planLineItem = lineItems.find(li => li.product.category === PRODUCT_CATEGORIES.MEMBERSHIP);

  const { country, state, shippingMethod } = shippingStageState;
  const [errorMessage, setErrorMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  useEffect(() => {
    trackingScripts.hotjar(isProduction, "/orders/buy-now/review_stage");
    trackingScripts.facebook(
      isProduction,
      FACEBOOK_EVENTS.REACT_PAGE_VIEW,
      { page: "ReviewStage" },
      "trackCustom"
    );
    }, []);

  const getLastFourCharOfString = (string) => {
    return string.slice(string.length - 4);
  };

  const createStripeOrder = () => {
    if (isProcessing)
      return;

    setIsProcessing(true);

    // Reset the error message while we attempt to create the order.
    setErrorMessage(null);

    // Calculate the total price of the order so the client's value can be compared to the server's value.
    const totalPrice = calcTotalPrice(lineItems, country, state, shippingMethod, salesTaxRates, promoCode);

    stripeApi.createOrder(portal, purchaseFlow, shippingStageState, paymentStageState, lineItems, promoCode, totalPrice, referralCode, giftSenderInformation, adminReferralTargetCode, npi, deferredUserCreate)
      .then(response => {
        if (response.status === 'PROCESSED') {
          if (response.confirmationToken != null) {
            // When the customer has successfully made a purchase, remove them from Mailchimp as a potential customer.
            if (purchaseFlow === PURCHASE_FLOWS.SHAPA_END_USER) {
              mailChimpApi.updateUserTag(shippingStageState.email, MAIL_CHIMP_LIST_IDS.SHAPA_SUBSCRIBERS_LIST, MAIL_CHIMP_TAGS.ABANDONED_CART_TAG, MAIL_CHIMP_TAGS_STATUS.INACTIVE);
            }

            setOrder(response);
            setPurchaseStage(PURCHASE_STAGES.CONFIRMATION);
          } else {
            setErrorMessage('We were unable to process your order. Please contact Shapa customer support.');
          }
        } else if (response.status === 'DECLINED') {
          setErrorMessage('It seems your payment was declined. Please ensure your payment information is correct. We apologize for the inconvenience.');
        } else {
          // Fail-safe error message.
          setErrorMessage('We are currently unable to process your payment. Please try again later.');
        }
      })
      .catch(response => {
        setErrorMessage(response.responseText);
      })
      .finally(() => {
        setIsProcessing(false);
      });
  };

  // The primary text class used within the order review.
  const primaryTextClass = "plan-main-txt fw-bolder text-dark mb-0 pb-0";

  // Decide which product to show in the preview component.
  let previewProduct;
  if (scaleLineItem)
    previewProduct = scaleLineItem.product;
  else if (adminAccessLineItem)
    previewProduct = adminAccessLineItem.product;
  else
    previewProduct = scaleProducts[0];

  return (
    <React.Fragment>
      <p className="offset-lg-1 col-lg-10 header-txt text-dark mt-4 mb-2 my-lg-0 pt-3 pt-lg-0">Review your order</p>

      <div className="offset-lg-1 col-lg-3 d-none d-lg-block ms-lg-5 mt-lg-n4">
        <ProductPreview product={previewProduct} className="py-lg-5" />
      </div>

      <div className="col-lg-4 my-2 my-lg-0">
        {lineItems.map(lineItem => <LineItemDisplay key={lineItem.product.id} lineItem={lineItem} textClass={primaryTextClass} />)}

        {scaleLineItem &&
          <div className="row justify-content-between">
            <div className="col-6 d-flex flex-column">
              <p className={primaryTextClass}>Shipping</p>
            </div>
            <div className="col-2 text-center">
              {quantitiesEnabled && <p className={primaryTextClass}>{scaleLineItem.quantity}</p>}
            </div>
            <div className="col-4 text-end">
              <p className="plan-main-txt fw-bolder text-dark">${<ShippingPrice/>}</p>
            </div>
          </div>
        }

        <div className="row justify-content-between">
          <div className="col-9 d-flex flex-column">
            <p className={primaryTextClass}>Sales tax</p>
          </div>
          <div className="col-3 text-end">
            <p className="plan-main-txt fw-bolder text-dark">${<SalesTaxPrice />}</p>
          </div>
        </div>

        <PromoCode hideDecimalIfWhole={false} disableEntry={true} />
        <ReferralCode />

        <div className="row justify-content-between h-auto">
          <div className="col-8 d-flex flex-column">
            <p className={primaryTextClass}>Total</p>
          </div>
          <div className="col-4 text-end">
            <p className="plan-main-txt fw-bolder text-dark pb-0 mb-0">
              ${<TotalPrice />}
            </p>
          </div>
        </div>
      </div>

      <div className="col-lg-3 my-2 my-lg-0">
        <div className="row justify-content-between">
          <div className="col-9 d-flex flex-column">
            <p className={primaryTextClass}>Shipping information</p>
            <p className="sub-txt text-muted">
              {scaleLineItem && shippingStageState.shippingMethod.label}
              {scaleLineItem && <br/>}
              {shippingStageState.firstName} {shippingStageState.lastName}
              <br/>
              {shippingStageState.address1}
              <br/>
              {shippingStageState.address2 && shippingStageState.address2}
              {shippingStageState.address2 && <br/>}
              {shippingStageState.city}, {shippingStageState.state} {shippingStageState.zipCode}
            </p>
          </div>
          <div className="col-3 text-end">
            <p className="plan-main-txt fw-bolder main-color cursor-pointer"
               onClick={() => setPurchaseStage(PURCHASE_STAGES.SHIPPING)}>Edit</p>
          </div>
        </div>
        <div className="row justify-content-between">
          <div className="col-9 d-flex flex-column">
            <p className={primaryTextClass}>Payment information</p>
            <p className="sub-txt text-muted pb-0">Visa ending in {getLastFourCharOfString(paymentStageState.creditCardNumber)}</p>
            <p className="sub-txt text-muted pb-0">{paymentStageState.nameOnCard}</p>
            {
              paymentStageState.isUsingBillingAddress && <p className="sub-txt text-muted pb-0">
                {paymentStageState.paymentInputAddress}
                <br/>
                {paymentStageState.paymentInputAddress2 && paymentStageState.paymentInputAddress2}
                {paymentStageState.paymentInputAddress2 && <br/>}
                {paymentStageState.paymentInputCity}, {paymentStageState.paymentInputState} {paymentStageState.paymentInputZip}
              </p>
            }
          </div>
          <div className="col-3 text-end">
            <p
              className="plan-main-txt fw-bolder main-color cursor-pointer"
              onClick={() => setPurchaseStage(PURCHASE_STAGES.PAYMENT)}>
              Edit
            </p>
          </div>
        </div>

        {planLineItem && planLineItem.product.recurringPlan &&
          <div className="row justify-content-between h-auto d-inline">
            <p className="sub-txt text-muted pb-lg-0 mb-lg-0 mt-lg-3 text-center text-lg-left">
              Your subscription will automatically renew unless you cancel
              before {formattedNextChargeDate(planLineItem.product, promoCode)}.
              Your cancellation will take effect at the end of your current billing cycle.
            </p>
          </div>
        }
      </div>

      <div className="offset-lg-1 col-lg-3 d-none d-lg-flex ms-lg-5 justify-content-center">
        <a
          className="sub-txt align-bottom fw-bolder border-bottom-solid text-dark back-link align-self-end"
          onClick={() => setPurchaseStage(PURCHASE_STAGES.PAYMENT)}
        >
          Return to payment
        </a>
      </div>
      <div className="col-lg-4 align-items-center justify-content-center text-center px-lg-4">
        <p className="text-center plan-main-txt text-dark fw-bold d-none d-lg-block">
          Your card will be charged ${<TotalPrice />}
        </p>

        <p className="plan-main-txt text-danger">
          <br/>
          {errorMessage}
        </p>

        <a>
          <div
            className="teal button-link next-button-txt next-step-button align-self-center text-center px-lg-0 mt-lg-2"
            onClick={() => createStripeOrder()}
          >
            {isProcessing ? "Processing..." : "Place your order"}
          </div>
        </a>
      </div>
    </React.Fragment>
  );
};

export default ReviewStage;